// A `MarkGraphics` that writes SVG path markup (R1844).
//
// The hull marks of `marks.ts` and `hull-styles.ts` are descriptions over
// the `MarkGraphics` surface. The atlas bakes them on a 2D canvas, and the
// DOM chrome needs the same shapes as markup: the fleet row of FLEET ·
// MISSIONS draws the hull mark of the class beside the class word. This
// surface records the path calls into one SVG `d` string and writes a
// `<path>` at each paint, in `currentColor`, so the element that holds the
// mark sets the ink once and no colour stands in the markup.
//
// It copies the path rules of `Canvas2DMarks`: path calls accumulate until
// a paint, a paint keeps the current point, an `arc()` from a current point
// draws a line to the start of the arc, and a `circle()` is a closed
// sub-path of its own that leaves no current point. The fill rule is
// nonzero, as on the canvas, so a `ring()` keeps its hole.
//
// A mark of several inks, such as a structure symbol with its glyph knocked
// out of the core in the water's dark (R1987), takes `{ colors: true }`: each
// paint then writes the colour of its style, as the atlas bakes it. `reach()`
// states how far from the origin the paint goes, so a caller can size the
// box of a mark that the map draws at its cell centre.

import type { MarkFill, MarkGraphics, MarkStroke } from './marks';
import { hex } from './color';
import { TAU } from './math';

/** A coordinate in the markup: two decimals, and no "-0". */
function n(v: number): string {
  const r = Math.round(v * 100) / 100;
  return String(r === 0 ? 0 : r);
}

/** The SVG markup of the paths that a mark description paints. */
export class SvgPathMarks implements MarkGraphics {
  private d: string[] = [];
  private cur: [number, number] | null = null;
  private start: [number, number] | null = null;
  private out: string[] = [];
  private readonly colors: boolean;
  /** The reach of the path calls since the last paint, and of every paint. */
  private pathReach = 0;
  private paintReach = 0;

  /** `colors` writes the colour of each paint in place of `currentColor`. */
  constructor(opts: { colors?: boolean } = {}) {
    this.colors = opts.colors ?? false;
  }

  /** Widens the reach of the current path to a box about (x, y) of half side r. */
  private reachTo(x: number, y: number, r = 0): void {
    this.pathReach = Math.max(this.pathReach, Math.abs(x) + r, Math.abs(y) + r);
  }

  /** The ink attribute value of one paint. */
  private ink(color: number): string {
    return this.colors ? hex(color & 0xffffff) : 'currentColor'; // mask: the 24 bits
  }

  moveTo(x: number, y: number): this {
    this.reachTo(x, y);
    this.d.push(`M${n(x)} ${n(y)}`);
    this.cur = [x, y];
    this.start = [x, y];
    return this;
  }

  lineTo(x: number, y: number): this {
    if (!this.cur) return this.moveTo(x, y);
    this.reachTo(x, y);
    this.d.push(`L${n(x)} ${n(y)}`);
    this.cur = [x, y];
    return this;
  }

  arc(x: number, y: number, r: number, a0: number, a1: number, ccw = false): this {
    const sx = x + Math.cos(a0) * r, sy = y + Math.sin(a0) * r;
    // The whole circle bounds the arc: a reach may be wide, never short.
    this.reachTo(x, y, r);
    if (this.cur) this.lineTo(sx, sy);
    else this.moveTo(sx, sy);
    // The span in the direction of travel, as a browser canonicalises it:
    // an end behind the start goes round the other way, so a
    // counterclockwise arc from 0 to 2π is a whole turn. An end a whole
    // number of turns behind is a whole turn too: Blink and WebKit
    // (`adjustEndAngle`) and Gecko (`ArcToBezier`) all add 2π − (−span mod
    // 2π), which is 2π there, although the spec text reads it as no arc
    // (R2239).
    let span = ccw ? a0 - a1 : a1 - a0;
    if (span < 0) span = TAU - ((-span) % TAU);
    const sweep = ccw ? 0 : 1;
    if (span >= TAU) {
      // A whole turn: two half arcs, because one SVG arc cannot close on
      // its own start.
      const mid = a0 + (ccw ? -Math.PI : Math.PI);
      this.d.push(`A${n(r)} ${n(r)} 0 1 ${sweep} ${n(x + Math.cos(mid) * r)} ${n(y + Math.sin(mid) * r)}`);
      this.d.push(`A${n(r)} ${n(r)} 0 1 ${sweep} ${n(sx)} ${n(sy)}`);
      this.cur = [sx, sy];
      return this;
    }
    const ex = x + Math.cos(a1) * r, ey = y + Math.sin(a1) * r;
    this.d.push(`A${n(r)} ${n(r)} 0 ${span > Math.PI ? 1 : 0} ${sweep} ${n(ex)} ${n(ey)}`);
    this.cur = [ex, ey];
    return this;
  }

  closePath(): this {
    this.d.push('Z');
    this.cur = this.start;
    return this;
  }

  poly(points: number[], close = true): this {
    if (points.length < 4) return this;
    this.moveTo(points[0], points[1]);
    for (let i = 2; i + 1 < points.length; i += 2) this.lineTo(points[i], points[i + 1]);
    if (close) this.closePath();
    return this;
  }

  circle(x: number, y: number, r: number): this {
    this.reachTo(x, y, r);
    this.d.push(`M${n(x + r)} ${n(y)}A${n(r)} ${n(r)} 0 1 1 ${n(x - r)} ${n(y)}`
      + `A${n(r)} ${n(r)} 0 1 1 ${n(x + r)} ${n(y)}Z`);
    this.cur = null;
    this.start = null;
    return this;
  }

  fill(style: MarkFill): this {
    if (this.d.length > 0) {
      const a = style.alpha ?? 1;
      this.out.push(`<path d="${this.d.join('')}" fill="${this.ink(style.color)}"`
        + (a < 1 ? ` fill-opacity="${n(a)}"` : '') + '/>');
      this.paintReach = Math.max(this.paintReach, this.pathReach);
    }
    this.d = [];
    this.pathReach = 0;
    return this;
  }

  stroke(style: MarkStroke): this {
    if (this.d.length > 0) {
      const a = style.alpha ?? 1;
      this.out.push(`<path d="${this.d.join('')}" fill="none" stroke="${this.ink(style.color)}"`
        + ` stroke-width="${n(style.width)}"`
        + (style.join ? ` stroke-linejoin="${style.join}"` : '')
        + (style.cap ? ` stroke-linecap="${style.cap}"` : '')
        + (a < 1 ? ` stroke-opacity="${n(a)}"` : '') + '/>');
      this.paintReach = Math.max(this.paintReach, this.pathReach + style.width / 2);
    }
    this.d = [];
    this.pathReach = 0;
    return this;
  }

  /**
   * How far from the origin the paints so far reach, on either axis, with
   * half the stroke width. An arc counts as its whole circle.
   */
  reach(): number {
    return this.paintReach;
  }

  /** The `<path>` elements painted so far. */
  markup(): string {
    return this.out.join('');
  }
}
